import request from 'umi-request'
import Cookies from 'js-cookie'
import {
    addUser,
    logoutUser,
} from '../actions'
import { APIRequest } from './helpers/requests'
import { API } from '../config'

/*
 * Auth service: login, logout, refresh, token getters.
 * 401 handling and request retry are in requests.jsx (single source of truth).
 */
export const authService = {
    login,
    logout,
    refreshLogin,
    register,
    resendActivation,
    getAccessToken,
    validateToken,
    getUserProfile
}

async function getUserProfile() {
    return await APIRequest({
        url: API.USER_ME,
        method: 'GET',
        data: {},
        private: true
    });
}

/** Default access token lifetime in seconds when refresh returns only { access } (no expiresIn). */
const DEFAULT_ACCESS_COOKIE_SECONDS = 10 * 60

function setAuthTokens(response) {
    if (!response || !response.access) return

    const expiresInSeconds = typeof response.expiresIn === 'number' && response.expiresIn > 0
        ? response.expiresIn
        : DEFAULT_ACCESS_COOKIE_SECONDS
    const expiresAt = new Date(Date.now() + expiresInSeconds * 1000)

    const cookieOpts = (secure) => (secure ? { expires: expiresAt, secure: true } : { expires: expiresAt })
    const isSecure = document.location.protocol === 'https:'

    Cookies.set('accessToken', response.access, cookieOpts(isSecure))

    if (response.refresh) {
        if (isSecure) {
            Cookies.set('refreshToken', response.refresh, { secure: true })
        } else {
            Cookies.set('refreshToken', response.refresh)
        }
    }
}

async function login(username, password, dispatch, i18n) {
    let result = await APIRequest({
        url: API.LOGIN,
        method: 'POST',
        data: {
            username: username,
            password: password,
        },
        private: false,
    })

    if (result.status === 200) {
        setAuthTokens(result.response)
        const userMe = await getUserProfile()
        if (userMe.status === 200) {
            dispatch(addUser(userMe.response))
            result['role'] = userMe.response.role
        } else {
            dispatch(addUser(result.response.user))
        }
    }

    return result
}

async function refreshLogin(refreshToken) {
    // Same transport/parse path as login (APICall + getResponse). Do not strip cookies until we have a new access token.
    const result = await APIRequest({
        url: API.REFRESH,
        method: 'POST',
        data: { refresh: refreshToken },
        private: false,
    })
    if (result.status !== 200 || !result.response?.access) {
        Cookies.remove('accessToken')
        Cookies.remove('refreshToken')
        return false
    }
    setAuthTokens({
        ...result.response,
        refresh: result.response.refresh || refreshToken,
    })
    return true
}

function logout(dispatch) {
    // Perform user logout and remove user from redux store and cookies
    Cookies.remove('accessToken')
    Cookies.remove('refreshToken')
    dispatch(logoutUser())
    dispatch({ type: 'RESET_STATE' })
}

/**
 * Returns a valid access token or false. Uses APIRequest (verify) which already does
 * 401 → refresh → retry. We only call refreshLogin here when we had no access token
 * (so APIRequest did not run refresh); otherwise we rely on APIRequest as single source.
 */
async function validateToken() {
    const token = Cookies.get('accessToken')
    const refresh = Cookies.get('refreshToken')

    const response = await APIRequest({
        url: API.VERIFY,
        method: 'POST',
        data: { token: token || '' },
        private: !!token,
    })

    if (response && response.status === 200) {
        return Cookies.get('accessToken')
    }
    if (!token && refresh) {
        const ok = await refreshLogin(refresh)
        return ok ? Cookies.get('accessToken') : false
    }
    return false
}

async function getAccessToken() {
    return await validateToken()
}

/* Register */
async function register(data) {
    let created
    created = await request(API.REGISTER, {
        method: 'post',
        data: data,
    })
        .then(function (response) {
            return true
        })
        .catch(function (error) {
            return false
        })

    return created
}

async function resendActivation(data) {
    let sended
    sended = await request(API.RESEND_ACTIVATION, {
        method: 'post',
        data: data,
    })
        .then(function (response) {
            return true
        })
        .catch(function (error) {
            return false
        })

    return sended
}

export async function checkUser() {
    const result = await getAccessToken()
    return !result ? false : true
}
